function words(text: string): string[] {
  return text.trim().split(/\s+/).filter(Boolean)
}

function comparableWord(word: string): string {
  return word
    .toLocaleLowerCase('en-US')
    .replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, '')
}

function comparableWords(text: string): string[] {
  return words(text).map(comparableWord).filter(Boolean)
}

function tokenPrefix(prefix: string[], full: string[]): boolean {
  if (prefix.length > full.length) return false
  return prefix.every((word, index) => full[index] === word)
}

function longestTokenOverlap(left: string[], right: string[]): number {
  const max = Math.min(left.length, right.length)
  for (let size = max; size > 0; size -= 1) {
    let matched = true
    for (let offset = 0; offset < size; offset += 1) {
      if (left[left.length - size + offset] !== right[offset]) {
        matched = false
        break
      }
    }
    if (matched) return size
  }
  return 0
}

export function appendDictationText(existing: string, delta: string): string {
  const incoming = existing ? delta : delta.trimStart()
  if (!incoming.trim()) return existing
  if (!existing) return incoming
  if (/\s$/.test(existing) || /^\s/.test(incoming) || /^[,.;:!?)]/.test(incoming)) {
    return existing + incoming
  }
  return `${existing} ${incoming}`
}

export function mergeDictationText(existing: string, incoming: string): string {
  if (!incoming.trim()) return existing
  if (!existing.trim()) return incoming.trimStart()

  const existingComparable = comparableWords(existing)
  const incomingComparable = comparableWords(incoming)
  if (incomingComparable.length === 0) return existing
  if (existingComparable.length === 0) return appendDictationText(existing, incoming)

  if (tokenPrefix(incomingComparable, existingComparable)) {
    return existing
  }

  if (tokenPrefix(existingComparable, incomingComparable)) {
    const suffix = words(incoming).slice(existingComparable.length).join(' ')
    return suffix ? appendDictationText(existing, suffix) : existing
  }

  const overlap = longestTokenOverlap(existingComparable, incomingComparable)
  if (overlap > 0) {
    const suffix = words(incoming).slice(overlap).join(' ')
    return suffix ? appendDictationText(existing, suffix) : existing
  }

  return appendDictationText(existing, incoming)
}

export function commitDictationFinal(
  committedText: string,
  visibleInterimText: string,
  finalText: string,
): string {
  const stableFinalText = visibleInterimText.trim()
    ? mergeDictationText(visibleInterimText, finalText)
    : finalText
  return mergeDictationText(committedText, stableFinalText)
}

export function preserveDictationProgress(previousText: string, proposedText: string): string {
  if (!previousText.trim()) return proposedText.trimStart()
  if (!proposedText.trim()) return previousText
  return mergeDictationText(previousText, proposedText)
}
